/**
 * Authentication API Functions
 * Handles all authentication-related API calls
 */

import apiClient from './client';
import type {
  LoginRequest,
  LoginResponse,
  McpGatewayLoginHandoffRequest,
  McpGatewayLoginHandoffResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  UpdateProfileRequest,
  SessionsResponse,
  RevokeSessionsResponse,
} from '@/types/api.types';
import type { User } from '@/store';

/** Map backend role names to frontend uppercase literals */
const ROLE_MAP: Record<string, User['role']> = {
  'Super Admin': 'ADMIN',
  'Organisation Admin': 'ADMIN',
  Admin: 'ADMIN',
  Manager: 'MANAGER',
  User: 'USER',
};

function getRawPlatformRole(user: { role: unknown; platformRole?: unknown }): string {
  if (typeof user.platformRole === 'string' && user.platformRole.trim().length > 0) {
    return user.platformRole;
  }

  if (user.role && typeof user.role === 'object') {
    const role = user.role as { name?: unknown };
    if (typeof role.name === 'string' && role.name.trim().length > 0) {
      return role.name;
    }
  }

  if (typeof user.role === 'string' && user.role.trim().length > 0) {
    return user.role;
  }

  return 'User';
}

/**
 * Normalize a user object from the API.
 * Handles two backend shapes:
 *  - role as string ("Admin")
 *  - role as object ({ id, name, ... })
 * Converts to the uppercase literal the frontend store expects.
 */
function normalizeUserRole<T extends { role: unknown; platformRole?: unknown }>(
  user: T
): T & { role: User['role']; platformRole: string } {
  const raw = getRawPlatformRole(user);
  return {
    ...user,
    role: ROLE_MAP[raw] ?? 'USER',
    platformRole: raw,
  };
}

/**
 * Normalize a raw user response from the backend into the frontend User shape.
 * Applies both role normalization and status defaulting so that all API
 * functions (getCurrentUser, updateProfile) return a consistent User object.
 */
function normalizeUser(data: Record<string, unknown>): User {
  const withRole = normalizeUserRole(data as { role: unknown });
  return {
    ...withRole,
    status: (withRole as { status?: string }).status ?? 'ACTIVE',
  } as User;
}

/**
 * Login with email and password
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
  return { ...response.data, user: normalizeUserRole(response.data.user) };
};

export const createMcpGatewayLoginHandoff = async (
  data: McpGatewayLoginHandoffRequest
): Promise<McpGatewayLoginHandoffResponse> => {
  const response = await apiClient.post<McpGatewayLoginHandoffResponse>(
    '/auth/mcp-gateway-login-handoff',
    data
  );
  return response.data;
};

/**
 * Register a new user account
 */
export const register = async (data: RegisterRequest): Promise<RegisterResponse> => {
  const response = await apiClient.post<RegisterResponse>('/auth/register', data);
  return { ...response.data, user: normalizeUserRole(response.data.user) };
};

/**
 * Logout current user
 */
export const logout = async (refreshToken?: string): Promise<void> => {
  await apiClient.post('/auth/logout', { refreshToken });
};

/**
 * Refresh access token using refresh token
 */
export const refreshToken = async (refreshTokenValue: string): Promise<RefreshTokenResponse> => {
  const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', {
    refreshToken: refreshTokenValue,
  });
  return response.data;
};

/**
 * Request password reset email
 */
export const forgotPassword = async (
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
  return response.data;
};

/**
 * Reset password with token
 */
export const resetPassword = async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<ResetPasswordResponse>('/auth/reset-password', data);
  return response.data;
};

/**
 * Change password for authenticated user
 */
export const changePassword = async (
  data: ChangePasswordRequest
): Promise<ChangePasswordResponse> => {
  const response = await apiClient.post<ChangePasswordResponse>('/auth/change-password', data);
  return response.data;
};

/**
 * Get current authenticated user profile
 */
export const getCurrentUser = async (): Promise<User> => {
  // Use /auth/me (not /users/me) because workspace context may not
  // be available during initial auth restore after a hard refresh.
  const response = await apiClient.get<Record<string, unknown>>('/auth/me');
  return normalizeUser(response.data);
};

/**
 * Update current user profile
 */
export const updateProfile = async (data: UpdateProfileRequest): Promise<User> => {
  const response = await apiClient.patch<Record<string, unknown>>('/users/me', data);
  return normalizeUser(response.data);
};

/**
 * Get active sessions
 */
export const getSessions = async (): Promise<SessionsResponse> => {
  const response = await apiClient.get<SessionsResponse>('/auth/sessions');
  return response.data;
};

/**
 * Revoke all sessions (logout everywhere)
 */
export const revokeAllSessions = async (): Promise<RevokeSessionsResponse> => {
  const response = await apiClient.post<RevokeSessionsResponse>('/auth/sessions/revoke-all');
  return response.data;
};
